import React, { useEffect, useState, useMemo } from 'react';
import { Globe, X } from 'lucide-react';
import { useZakatGlobal } from '../context/ZakatGlobalContext';

const STORAGE_KEY = 'zi-lang-prompt-v1';

// Native UI strings — kept self-contained so the prompt itself is always
// rendered in the LANGUAGE BEING OFFERED, regardless of current selection.
const STRINGS = {
  en: { q: 'View this site in English?',          accept: 'Switch to English',    dismiss: 'Keep current', rules: 'See your country\u2019s legal rules' },
  ar: { q: 'هل تريد عرض هذا الموقع بالعربية؟',     accept: 'التبديل إلى العربية',  dismiss: 'إبقاء الحالي', rules: 'انظر القواعد القانونية لبلدك' },
  ur: { q: 'کیا یہ سائٹ اردو میں دیکھنا چاہتے ہیں؟', accept: 'اردو میں دیکھیں',     dismiss: 'موجودہ زبان رکھیں', rules: 'اپنے ملک کے قانونی قواعد دیکھیں' },
  fr: { q: 'Voir ce site en français ?',           accept: 'Passer au français',   dismiss: 'Conserver', rules: 'Voir les règles juridiques de votre pays' },
  ms: { q: 'Lihat laman ini dalam Bahasa Melayu?', accept: 'Tukar ke Bahasa Melayu', dismiss: 'Kekal', rules: 'Lihat peraturan undang-undang negara anda' },
  tr: { q: 'Bu siteyi Türkçe görüntülemek ister misiniz?', accept: 'Türkçeye geç', dismiss: 'Şu anki dilde kal', rules: 'Ülkenizin hukuk kurallarını görün' },
  ru: { q: 'Просмотреть сайт на русском?',         accept: 'Перейти на русский',   dismiss: 'Оставить как есть', rules: 'Посмотреть правовые нормы вашей страны' },
  // ----- Feb 2026 -----
  hi: { q: 'क्या यह साइट हिन्दी में देखना चाहते हैं?',  accept: 'हिन्दी में देखें',   dismiss: 'मौजूदा भाषा रखें', rules: 'अपने देश के क़ानूनी नियम देखें' },
  bn: { q: 'এই সাইটটি বাংলায় দেখতে চান?',          accept: 'বাংলায় দেখুন',     dismiss: 'বর্তমান রাখুন', rules: 'আপনার দেশের আইনি নিয়ম দেখুন' },
  ml: { q: 'ഈ സൈറ്റ് മലയാളത്തിൽ കാണാൻ ആഗ്രഹിക്കുന്നുവോ?', accept: 'മലയാളത്തിൽ കാണുക', dismiss: 'നിലവിലുള്ളത് സൂക്ഷിക്കുക', rules: 'നിങ്ങളുടെ രാജ്യത്തിന്റെ നിയമ നിയമങ്ങൾ കാണുക' },
  id: { q: 'Lihat situs ini dalam Bahasa Indonesia?', accept: 'Beralih ke Indonesia', dismiss: 'Pertahankan saat ini', rules: 'Lihat peraturan hukum negara Anda' },
};

// Generic "choose your language" header used for multi-option mode.
const PICKER_HEADER = {
  en: 'Choose your language', ar: 'اختر لغتك',  ur: 'اپنی زبان منتخب کریں',  fr: 'Choisissez votre langue',
  ms: 'Pilih bahasa anda',    tr: 'Dilinizi seçin', ru: 'Выберите ваш язык',
  hi: 'अपनी भाषा चुनें', bn: 'আপনার ভাষা বেছে নিন', ml: 'നിങ്ങളുടെ ഭാഷ തിരഞ്ഞെടുക്കുക', id: 'Pilih bahasa Anda',
};

const LanguagePrompt = () => {
  const { selectedLanguage, selectedCountry, updateLanguage, languages } = useZakatGlobal();
  const [suggested, setSuggested] = useState(null); // language object to offer
  const [open, setOpen] = useState(false);

  // Detect a preferred language based on (a) the detected country's default
  // language, with (b) navigator.languages as a backup. Runs on first mount
  // AND when the country resolves async via ipwho.is.
  useEffect(() => {
    // Already dismissed / decided?
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch (_) { /* ignore */ }

    // User explicitly picked a LANGUAGE → never re-prompt them.
    // (Picking only a country doesn't count — they might still want the
    // local language offer.)
    try {
      const lock = JSON.parse(localStorage.getItem('zi-locale-v1') || 'null');
      if (lock && lock.languageExplicit) return;
    } catch (_) { /* ignore */ }

    // If the URL explicitly carries ?lang=, treat as an intentional choice — don't prompt.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('lang')) return;
    } catch (_) { /* ignore */ }

    // 1) Country-based preference (primary signal per product requirements)
    let preferredCode = selectedCountry?.defaultLanguage || null;

    // 2) Browser language as a backup if country detection hasn't resolved
    if (!preferredCode) {
      const navLangs = (navigator.languages && navigator.languages.length)
        ? navigator.languages
        : [navigator.language || 'en'];
      for (const raw of navLangs) {
        const code = (raw || '').slice(0, 2).toLowerCase();
        if (languages.find((l) => l.code === code)) {
          preferredCode = code;
          break;
        }
      }
    }
    if (!preferredCode) return;

    const match = languages.find((l) => l.code === preferredCode);
    if (!match) return;

    // Multi-language countries (3+ languages) should show the picker even
    // when the visitor is already in the default language — otherwise
    // alternate locales (e.g. ar/tr for DE-en visitors) become unreachable
    // from the prompt. The picker handles its own "no-op if same language"
    // semantics inside each chip's onClick.
    const isMulti = (selectedCountry?.languages || []).length >= 3;
    if (!isMulti && selectedLanguage && match.code === selectedLanguage.code) return;

    setSuggested(match);
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [languages, selectedLanguage, selectedCountry]);

  const strings = useMemo(() => {
    if (!suggested) return null;
    return STRINGS[suggested.code] || STRINGS.en;
  }, [suggested]);

  // Multi-language picker mode — triggered when the detected country
  // offers 3 or more languages (e.g. India: ur/hi/ml/en). Shows a chip
  // row so the visitor can pick from any of them with one tap, rather
  // than forcing a single-language accept/dismiss decision.
  const multiOptions = useMemo(() => {
    const codes = selectedCountry?.languages || [];
    if (codes.length < 3) return null;
    return codes
      .map((code) => languages.find((l) => l.code === code))
      .filter(Boolean);
  }, [selectedCountry, languages]);

  const pickerHeader = useMemo(() => {
    if (!suggested) return null;
    return PICKER_HEADER[suggested.code] || PICKER_HEADER.en;
  }, [suggested]);

  const persistDecision = (decision) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ decision, at: Date.now() }));
    } catch (_) { /* ignore */ }
  };

  const handleAccept = () => {
    if (suggested) updateLanguage(suggested.code);
    persistDecision('accepted');
    setOpen(false);
  };

  const handleDismiss = () => {
    persistDecision('dismissed');
    setOpen(false);
  };

  if (!open || !suggested || !strings) return null;

  const isRTL = suggested.dir === 'rtl';

  return (
    <div
      data-testid="language-prompt"
      dir={isRTL ? 'rtl' : 'ltr'}
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-sm
                 bg-[#EDF5F1] backdrop-blur-xl border border-gold-500/40 shadow-2xl
                 rounded-xl p-3 text-[#0D1520] animate-in fade-in slide-in-from-bottom-4"
      role="dialog"
      aria-live="polite"
      aria-label="Language suggestion"
    >
      <button
        data-testid="language-prompt-close"
        onClick={handleDismiss}
        className="absolute top-1.5 right-1.5 rtl:right-auto rtl:left-1.5 text-stone-400 hover:text-[#0D1520]"
        aria-label="Close"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start gap-2.5">
        <div className="shrink-0 w-7 h-7 rounded-full bg-gold-500/20 flex items-center justify-center">
          <Globe className="w-3.5 h-3.5 text-gold-400" />
        </div>
        <div className="flex-1 min-w-0 pr-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-[#0D4733]/80 mb-0.5">
            {multiOptions ? selectedCountry?.flag + ' ' + selectedCountry?.name : suggested.nameNative}
          </p>
          <p className="text-[13px] font-medium leading-snug">
            {multiOptions ? pickerHeader : strings.q}
          </p>

          {multiOptions ? (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="language-prompt-multi-row">
              {multiOptions.map((lang, idx) => (
                <button
                  key={lang.code}
                  data-testid={`language-prompt-pick-${lang.code}`}
                  onClick={() => {
                    updateLanguage(lang.code);
                    persistDecision(`picked-${lang.code}`);
                    setOpen(false);
                  }}
                  className={
                    'text-[12px] font-semibold px-2.5 py-1 rounded-full transition-colors ' +
                    (idx === 0
                      ? 'bg-gold-500 text-[#0D1520] hover:bg-gold-400'
                      : 'border border-stone-300 text-stone-700 hover:bg-[#EFE7D8]')
                  }
                >
                  {lang.nameNative}
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <button
                data-testid="language-prompt-accept"
                onClick={handleAccept}
                className="text-[12px] font-semibold px-2.5 py-1 rounded-full bg-gold-500 text-[#0D1520] hover:bg-gold-400 transition-colors"
              >
                {strings.accept}
              </button>
              <button
                data-testid="language-prompt-dismiss"
                onClick={handleDismiss}
                className="text-[12px] font-medium px-2.5 py-1 rounded-full border border-stone-300 text-stone-700 hover:bg-[#EFE7D8] transition-colors"
              >
                {strings.dismiss}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LanguagePrompt;
